/**
 * Move one column within the saved column order, from indexes into the reorder list the user sees.
 *
 * Those are two different lists. The reorder list can only show a selected id that resolves to a
 * field; one that does not — a custom field deleted since the view was saved — is dropped from it,
 * so every index after the gap is one ahead of the saved order. Translating through
 * `renderableColumnIds` keeps a drag on the column the user grabbed rather than whichever id
 * happens to sit at that index in the saved order.
 */
export function reorderSelectedColumns(
	selectedColumnIds: string[],
	renderableColumnIds: string[],
	renderedFromIndex: number,
	renderedToIndex: number
): string[] {
	const movingId = renderableColumnIds[renderedFromIndex];
	const targetId = renderableColumnIds[renderedToIndex];

	if (movingId === undefined || targetId === undefined) return selectedColumnIds;

	const reordered = [...selectedColumnIds];
	const fromIndex = reordered.indexOf(movingId);
	const toIndex = reordered.indexOf(targetId);

	if (fromIndex === -1 || toIndex === -1) return selectedColumnIds;

	reordered.splice(toIndex, 0, reordered.splice(fromIndex, 1)[0]);

	return reordered;
}

/**
 * The fields the reorder list draws, in saved order: one per selected id that resolves to a field.
 *
 * Both the list and the drag translation must come from this one call. Derived separately, a filter
 * added to either side would put the drag indexes out of step with the rows again.
 */
export function resolveRenderableColumns<Field extends { id: string }>(
	selectedColumnIds: string[],
	fields: Field[]
): Field[] {
	return selectedColumnIds.flatMap((columnId) => {
		const field = fields.find((candidate) => candidate.id === columnId);

		return field ? [field] : [];
	});
}
